import { inject } from "@angular/core"
import { HttpErrorResponse, HttpInterceptorFn } from "@angular/common/http"
import { ID_SYSTEM_TOKEN_INTERCEPTOR_CONFIG, IdSystemService, IdSystemTokenInterceptorUrlPattern, RefreshEvent } from "."
import { Observable, defer, filter, finalize, retry, switchMap, throwError } from "rxjs"


let refreshingObservable$: Observable<RefreshEvent> | undefined = undefined
let refreshing = false


export const idSystemTokenInterceptor: HttpInterceptorFn = (req, next) => {
  const idSystem = inject(IdSystemService)

  // Если токена нет, то продолжаем исходный запрос
  if (!idSystem.accessToken) {
    return next(req)
  }

  const configs = inject(ID_SYSTEM_TOKEN_INTERCEPTOR_CONFIG, {optional: true})

  // Если нет конфига, то продолжаем исходный запрос
  if (!configs) {
    return next(req)
  }

  const foundConfig = configs.find(config => matchUrl(req.url, config.urlPattern))

  // Если не найден подходящий конфиг, то продолжаем исходный запрос
  if (!foundConfig) {
    return next(req)
  }

  // Добавляем токен в заголовок
  const deferredReq = defer(() => {
    if (idSystem.accessToken) {
      return next(req.clone({
        headers: req.headers.set(
          foundConfig.headerName ?? 'Authorization',
          foundConfig.tokenType ? `${foundConfig.tokenType} ${idSystem.accessToken}` : idSystem.accessToken
        )
      }))
    }
    return next(req)
  })

  // Если в данный момент происходит обновление токена, то ожидаем обновления и делаем авторизованный запрос
  if (refreshing) {
    return getRefreshingObservable(idSystem).pipe(
      switchMap(() => deferredReq)
    )
  }

  // Делаем авторизованный запрос. Если он отваливается с 401, то пытаемся обновить токен и повторить запрос
  return deferredReq.pipe(
    retry({delay: error => {
      if (error instanceof HttpErrorResponse && error.status == 401) {
        refreshToken(idSystem)
        return getRefreshingObservable(idSystem)
      }
      return throwError(() => error)
    }})
  )
}


function getRefreshingObservable(idSystem: IdSystemService) {
  if (!refreshingObservable$) {
    refreshingObservable$ = idSystem.events$.pipe(
      filter((event): event is RefreshEvent => event.event === 'refresh')
    )
  }
  return refreshingObservable$
}

function refreshToken(idSystem: IdSystemService) {
  if (!refreshing) {
    refreshing = true
    idSystem.refreshToken().pipe(
      finalize(() => refreshing = false)
    ).subscribe()
  }
}

function matchUrl(url: string, pattern: IdSystemTokenInterceptorUrlPattern) {
  return typeof pattern === 'function' ? pattern(url) : typeof pattern === 'string' ? url.startsWith(pattern) : pattern.test(url)
}
